import { useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import {
    hideAppSpinner, showAppNotification, showAppSpinner,
} from '@rainbow-modules/app';
import { useOpenModal } from '@rainbow-modules/hooks';
import useHttpMutation from 'data/firestore/useHttpMutation';
import { EDIT_SIP_DOMAIN_DRAWER } from '../../constants';

interface SipDomain {
    domainSid: string;
    domainName: string;
}

interface UpdateDomainBody {
    workflowId: string;
}

const extractWorkflowId = (voiceUrl?: string): string | undefined => {
    if (!voiceUrl) return undefined;
    const match = voiceUrl.match(/\/workflows\/([^/]+)\/invoke/);
    return match?.[1];
};

const useManageSipDomains = ({ agentId }: { agentId: string }) => {
    const queryClient = useQueryClient();
    const [openEditForm, closeEditForm] = useOpenModal(EDIT_SIP_DOMAIN_DRAWER);

    const { mutateAsync: createSipDomain } = useHttpMutation<void, SipDomain>({
        pathname: `/agents/${agentId}/sip-domains`,
        method: 'POST',
    });

    const { mutateAsync: updateSipDomain } = useHttpMutation<UpdateDomainBody, unknown>({
        method: 'PATCH',
    });

    const handleCreate = useCallback(async () => {
        showAppSpinner();
        try {
            await createSipDomain({});
            await queryClient.invalidateQueries(['sip-domains', agentId]);
            showAppNotification({
                title: 'Success',
                description: 'SIP domain created successfully.',
                icon: 'success',
                timeout: 5000,
            });
        } catch (error) {
            const message = (error as any)?.message || 'Something went wrong. Please try again.';
            showAppNotification({
                title: 'Error',
                description: message,
                icon: 'error',
                timeout: 5000,
            });
        }
        hideAppSpinner();
    }, [agentId, createSipDomain, queryClient]);

    const handleEdit = useCallback(
        (domainSid: string, voiceUrl?: string): void => openEditForm({
            header: 'Edit SIP Domain',
            agentId,
            domainSid,
            initialValues: { workflowId: extractWorkflowId(voiceUrl) },
            onSubmit: async (values: UpdateDomainBody) => {
                showAppSpinner();
                try {
                    await updateSipDomain({
                        pathname: `/agents/${agentId}/sip-domains/${domainSid}`,
                        body: values,
                    });
                    await queryClient.invalidateQueries(['sip-domains', agentId]);
                    closeEditForm();
                    showAppNotification({
                        title: 'Success',
                        description: 'SIP domain updated successfully.',
                        icon: 'success',
                        timeout: 5000,
                    });
                } catch (error) {
                    const message = (error as any)?.message || 'Something went wrong. Please try again.';
                    showAppNotification({
                        title: 'Error',
                        description: message,
                        icon: 'error',
                        timeout: 5000,
                    });
                }
                hideAppSpinner();
            },
        }),
        [agentId, closeEditForm, openEditForm, queryClient, updateSipDomain],
    );

    return useMemo(() => ({
        create: handleCreate,
        edit: handleEdit,
        closeEditForm,
    }), [closeEditForm, handleCreate, handleEdit]);
};

export default useManageSipDomains;
